import { FC, ReactNode, useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Button } from '@kizen/kds/Button';
import { Icon, type ValidIcons } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getChosenBusiness,
  getManageIntegrationSecrets,
} from 'store/authentication/selectors';
import { INTEGRATION_SECRETS_ROUTE } from '__pages/Settings/pages/ManageConnections/subroutes';
import Notice from '__components/Kizen/Notice';
import {
  BUSINESS_APP_ACTIONS,
  EMPTY_VALUE,
} from 'ts-components/Plugins/businessApps/constants';
import {
  type BusinessAppAction,
  type BusinessAppPermissions,
  disabledNotice,
  formatDateTime,
  getVersionActions,
  inUseNotice,
} from 'ts-components/Plugins/businessApps/helpers';
import type { PendingBusinessAppAction } from 'ts-components/Plugins/businessApps/useBusinessAppActions';
import type {
  BusinessAppAdditions,
  BusinessAppDetails,
  BusinessAppVersion,
} from 'ts-components/Plugins/businessApps/types';
import { useActionLabels } from '../Apps/columns';
import { ComponentDependencies } from './ComponentDependencies';

type AdditionKey = Exclude<keyof BusinessAppAdditions, 'connections'>;

const ADDITION_ICONS: Record<AdditionKey | 'connections', ValidIcons> = {
  record_actions: 'text-block',
  automation_steps: 'asset-automations',
  dashboard_blocks: 'asset-dashboards',
  toolbar_items: 'nav-tab-my-toolbar',
  record_page_scripts: 'custom-file-lines',
  pages: 'custom-file',
  floating_frames: 'custom-window-restore',
  object_settings_menu_items: 'custom-bars',
  calendar_sources: 'custom-calendar',
  data_adornments: 'custom-tag',
  connections: 'nav-tab-connections',
  secrets: 'custom-key',
};

const Field: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-[12px]">
    <Typography weight="semibold">{label}</Typography>
    <Typography>{value}</Typography>
  </div>
);

const Section: FC<{
  title: string;
  tooltip?: string;
  children: ReactNode;
}> = ({ title, tooltip, children }) => (
  <section className="self-stretch mt-[40px]">
    <div className="mb-[20px] flex items-center gap-[10px]">
      <Typography variant="header" weight="semibold">
        {title}
      </Typography>
      {tooltip ? (
        <span role="img" aria-label={tooltip}>
          <Icon
            icon="message-info-circle"
            size="sm"
            color="font-placeholder"
            tooltipEnabled
            tooltipDescriptionOverride={tooltip}
          />
        </span>
      ) : null}
    </div>
    {children}
  </section>
);

const useAdditionLabels = () => {
  const { t } = useTranslation();
  return useMemo(
    (): Record<AdditionKey | 'connections', string> => ({
      record_actions: t('Record actions'),
      automation_steps: t('Automation steps'),
      dashboard_blocks: t('Dashboard blocks'),
      toolbar_items: t('Toolbar items'),
      record_page_scripts: t('Record page scripts'),
      pages: t('Pages'),
      floating_frames: t('Floating frames'),
      object_settings_menu_items: t('Object settings menu items'),
      calendar_sources: t('Calendar sources'),
      data_adornments: t('Data adornments'),
      connections: t('Connections'),
      secrets: t('Secrets'),
    }),
    [t]
  );
};

const AUTH_LEVEL_LABELS: Record<string, (t: TFunction) => string> = {
  business: (t) => t('business-wide'),
  user: (t) => t('per user'),
};

const WhatThisAppAdds: FC<{ details: BusinessAppDetails }> = ({ details }) => {
  const { t } = useTranslation();
  const labels = useAdditionLabels();
  const { connections, ...rest } = details.what_this_app_adds;
  const entries = (Object.keys(labels) as (AdditionKey | 'connections')[])
    .map((key) => ({
      key,
      values:
        key === 'connections'
          ? connections.map((connection) =>
              [
                connection.display_name || connection.service_name,
                connection.auth_level &&
                  `(${AUTH_LEVEL_LABELS[connection.auth_level]?.(t) ?? connection.auth_level})`,
              ]
                .filter(Boolean)
                .join(' ')
            )
          : rest[key],
    }))
    .filter(({ values }) => values?.length);

  if (!entries.length) {
    return <Typography>{t('This app does not add anything yet.')}</Typography>;
  }

  return (
    <div className="grid grid-cols-3 gap-[30px]">
      {entries.map(({ key, values }) => (
        <div className="flex items-start gap-[15px]" key={key}>
          <Icon icon={ADDITION_ICONS[key]} size="md" />
          <Field label={labels[key]} value={values.join(', ')} />
        </div>
      ))}
    </div>
  );
};

type AppDetailsPanelProps = {
  details: BusinessAppDetails;
  version?: BusinessAppVersion;
  permissions: BusinessAppPermissions;
  currentUserId?: string;
  onSelectAction: (request: PendingBusinessAppAction) => void;
};

export const AppDetailsPanel: FC<AppDetailsPanelProps> = ({
  details,
  version,
  permissions,
  currentUserId,
  onSelectAction,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const secretsAccess = useSelector(getManageIntegrationSecrets);
  const business = useSelector(getChosenBusiness);
  const disabledMessage = details.is_preview
    ? null
    : disabledNotice(t, details);
  const notice =
    disabledMessage ??
    inUseNotice(t, details, version, business?.name ?? '', currentUserId);
  const canSwitch =
    version &&
    getVersionActions(details, version, permissions, currentUserId, {
      includeView: false,
    }).includes(BUSINESS_APP_ACTIONS.useVersion);

  return (
    <div className="flex flex-col items-start">
      <div className="self-stretch flex flex-wrap items-center justify-between gap-[20px]">
        <Typography size="2xl" weight="semibold" variant="header">
          {details.name}
        </Typography>
        <div className="flex flex-wrap items-center gap-[20px]">
          <VersionActionButtons
            details={details}
            version={version}
            permissions={permissions}
            currentUserId={currentUserId}
            onSelectAction={onSelectAction}
          />
          {permissions.canPublishBusinessApps &&
          (secretsAccess.view || secretsAccess.edit) &&
          details.what_this_app_adds.secrets.length ? (
            <Button
              variant="text"
              color="primary"
              onClick={() => history.push(INTEGRATION_SECRETS_ROUTE)}
              qa={{ id: 'business-app-details-manage-secrets-button' }}
            >
              {t('Manage Secrets')}
            </Button>
          ) : null}
        </div>
      </div>
      {notice ? (
        <div className="self-stretch mt-[20px]">
          <Notice>
            <div className="flex items-center justify-between gap-[20px]">
              <div className="flex items-center gap-[10px]">
                <Icon
                  icon="message-info-circle"
                  size="sm"
                  color="feedback-info-foreground"
                />
                <Typography>{notice}</Typography>
              </div>
              {canSwitch ? (
                <Button
                  variant="text"
                  color="secondary"
                  preserveCase
                  maxWidth={false}
                  onClick={() =>
                    onSelectAction({
                      action: BUSINESS_APP_ACTIONS.useVersion,
                      app: details,
                      version,
                    })
                  }
                  qa={{ id: 'business-app-details-switch-version-button' }}
                >
                  {details.is_preview
                    ? t('Switch to This Version')
                    : t('Switch to Published Version')}
                </Button>
              ) : null}
            </div>
          </Notice>
        </div>
      ) : null}
      {details.external_link ? (
        <div className="mt-[30px]">
          <Typography
            variant="link"
            href={details.external_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {details.external_link}
          </Typography>
        </div>
      ) : null}
      <div className="grid grid-cols-[repeat(3,minmax(0,240px))] gap-[30px] mt-[30px]">
        <Field
          label={details.is_preview ? t('Created by') : t('Built by')}
          value={details.built_by?.full_name ?? EMPTY_VALUE}
        />
        {!details.is_preview && details.published_by ? (
          <Field
            label={t('Published by')}
            value={details.published_by.full_name ?? EMPTY_VALUE}
          />
        ) : null}
        <Field
          label={t('Last updated')}
          value={formatDateTime(t, details.last_updated)}
        />
      </div>
      {details.description ? (
        <Section title={t('About This App')}>
          <Typography preserveNewlines>{details.description}</Typography>
        </Section>
      ) : null}
      <Section
        title={
          details.is_preview
            ? t('What This Preview Adds')
            : t('What This App Adds')
        }
      >
        <WhatThisAppAdds details={details} />
      </Section>
      <Section
        title={t('Kizen Component Dependencies')}
        tooltip={t(
          "These assets are needed for the function of this app, but are shared components that aren't part of this app’s live/preview package."
        )}
      >
        <ComponentDependencies appId={details.api_name} />
      </Section>
      {!details.is_preview && details.support_email ? (
        <Section title={t('App Support')}>
          <Typography variant="link" href={`mailto:${details.support_email}`}>
            {details.support_email}
          </Typography>
        </Section>
      ) : null}
    </div>
  );
};

// Switching lives in the notice and Edit Configuration is the App Settings tab
const DETAILS_BUTTON_ACTIONS: BusinessAppAction[] = [
  BUSINESS_APP_ACTIONS.editWithKiCode,
  BUSINESS_APP_ACTIONS.publishPreview,
  BUSINESS_APP_ACTIONS.deletePreview,
  BUSINESS_APP_ACTIONS.disable,
  BUSINESS_APP_ACTIONS.enable,
];

type VersionActionButtonsProps = {
  details: BusinessAppDetails;
  version?: BusinessAppVersion;
  permissions: BusinessAppPermissions;
  currentUserId?: string;
  onSelectAction: (request: PendingBusinessAppAction) => void;
};

export const VersionActionButtons: FC<VersionActionButtonsProps> = ({
  details,
  version,
  permissions,
  currentUserId,
  onSelectAction,
}) => {
  const { t } = useTranslation();
  const actionLabels = useActionLabels();
  const labels: Partial<Record<BusinessAppAction, string>> = {
    ...actionLabels,
    disable: t('Disable App'),
    enable: t('Enable App'),
  };
  const actions = version
    ? getVersionActions(details, version, permissions, currentUserId, {
        includeView: false,
      }).filter((action) => DETAILS_BUTTON_ACTIONS.includes(action))
    : [];
  const buttonProps: Partial<
    Record<
      BusinessAppAction,
      { color: 'primary' | 'warning'; outline?: boolean }
    >
  > = {
    [BUSINESS_APP_ACTIONS.editWithKiCode]: { color: 'primary', outline: true },
    [BUSINESS_APP_ACTIONS.publishPreview]: { color: 'primary' },
    [BUSINESS_APP_ACTIONS.enable]: { color: 'primary', outline: true },
    [BUSINESS_APP_ACTIONS.disable]: { color: 'warning', outline: true },
    [BUSINESS_APP_ACTIONS.deletePreview]: { color: 'warning', outline: true },
  };

  return (
    <>
      {actions.map((action) => (
        <Button
          key={action}
          {...buttonProps[action]}
          onClick={() => onSelectAction({ action, app: details, version })}
          qa={{
            id: `business-app-details-${action.replace(/_/g, '-')}-button`,
          }}
        >
          {labels[action]}
        </Button>
      ))}
    </>
  );
};
